import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { accent, textPrimary, bgMuted } from './styles'
import WagerScreen from './WagerScreen'
import EventCard from './EventCard'
import PlacementZone from './PlacementZone'
import CurrentCard from './CurrentCard'
import RevealScreen from './RevealScreen'
import { findCorrectZoneIndex, loadWeekData, saveDailyResult, getTodayString, formatYear, isSunday, getApiUrl, getHomeUrl, getWeekKey, hubHeaders, DAILY_CREDIT, DAY_MULTIPLIER } from './gameLogic'
import { AlreadyPlayedPage } from '@precisionpros/game-ui'

const EVENT_PRO_GAME = { name: 'EventPro', wordmarkMain: 'Event', wordmarkAccent: 'Pro', badge: 'E', accent }

function EventProGame({ playerName, onLogout }) {
  const navigate = useNavigate()
  const buildProDomain = getApiUrl().includes('localhost') || getApiUrl().includes('127.0.0.1')
    ? 'http://localhost:3003'
    : 'https://buildpro.precisionpros.com'
  const buildProLink = `${buildProDomain}/?player_name=${encodeURIComponent(playerName || '')}`
  const [phase, setPhase] = useState('wager') // wager, playing, reveal, payout
  const [wager, setWager] = useState(null)
  const [yesterdaysBank, setYesterdaysBank] = useState(0)
  const [loading, setLoading] = useState(true)

  // Game state
  const [category, setCategory] = useState(null)
  const [anchor, setAnchor] = useState(null)
  const [pendingEvents, setPendingEvents] = useState([])
  const [placedEvents, setPlacedEvents] = useState([])
  const [selectedZone, setSelectedZone] = useState(null) // game logic index
  const [selectedZoneId, setSelectedZoneId] = useState(null) // unique visual id
  const [strikes, setStrikes] = useState([])

  // Load week data on mount
  useEffect(() => {
    const apiBaseUrl = getApiUrl()
    setLoading(true)

    // Fetch daily puzzle, player stats, and check if already played
    Promise.all([
      fetch(`${apiBaseUrl}/eventpro/daily/`, { headers: hubHeaders() }).then(res => res.json()),
      fetch(`${apiBaseUrl}/eventpro/player-stats/?player_name=${encodeURIComponent(playerName)}`, { headers: hubHeaders() }).then(res => res.json()),
      fetch(`${apiBaseUrl}/eventpro/has-played-today/?player_name=${encodeURIComponent(playerName)}`, { headers: hubHeaders() }).then(res => res.json())
    ])
    .then(([dailyData, playerData, playedData]) => {
      // Check if already played - before loading puzzle
      if (playedData.has_played) {
        setPhase('already-played')
        setLoading(false)
        return
      }

      // Set daily puzzle data
      setCategory(dailyData.category)
      setAnchor(dailyData.anchor)
      setPendingEvents(dailyData.events)

      // Set weekly bank from yesterday's stats
      const today = getTodayString()
      const weekStart = getWeekKey()

      const d = new Date(today)
      d.setDate(d.getDate() - 1)
      const yesterdayStr = d.toISOString().split('T')[0]

      // If yesterday is before the week starts, no bank to load
      if (yesterdayStr < weekStart) {
        setYesterdaysBank(0)
        setLoading(false)
        return
      }

      // Fetch recomputed weekly bank from leaderboard-stats (always fresh, never corrupted)
      fetch(`${apiBaseUrl}/eventpro/leaderboard-stats/?date=${yesterdayStr}`)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.json()
        })
        .then(statsData => {
          // Find player in weekly_leaderboard array
          if (statsData.weekly_leaderboard && Array.isArray(statsData.weekly_leaderboard)) {
            const playerEntry = statsData.weekly_leaderboard.find(entry => entry.player_name === playerName)
            if (playerEntry && playerEntry.weekly_bank !== undefined) {
              setYesterdaysBank(parseFloat(playerEntry.weekly_bank) || 0)
              setLoading(false)
              return
            }
          }
          // Player not found in stats, default to 0
          setYesterdaysBank(0)
          setLoading(false)
        })
        .catch(() => {
          // Fallback to 0 if stats fetch fails
          setYesterdaysBank(0)
          setLoading(false)
        })
    })
    .catch(() => {
      // Fallback to 0 if API fails entirely
      setYesterdaysBank(0)
      setLoading(false)
    })
  }, [playerName])

  // Header component
  const renderHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '12px 0', borderBottom: '1px solid #e0e0e0' }}>
      <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '500' }}>
        <span style={{ color: textPrimary }}>Event</span>
        <span style={{ color: accent }}>Pro</span>
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.85rem' }}>
        <div style={{ color: '#666' }}>{playerName}</div>
        <button onClick={() => navigate('/how-to-play')} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
          How to Play
        </button>
        <button onClick={() => navigate('/leaderboard')} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
          🏆 Leaderboard
        </button>
        <a href={buildProLink} style={{ color: '#0f766e', fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none' }}>
          BuildPro
        </a>
        <button onClick={() => window.location.href = getHomeUrl()} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
          Home
        </button>
      </div>
    </div>
  )

  const handleWagerSubmit = (wagerAmount) => {
    setWager(wagerAmount)
    setPhase('playing')
  }

  const currentEvent = pendingEvents[0]

  const handlePlaceEvent = () => {
    if (selectedZone === null || !currentEvent) return

    // Find correct position (accounting for anchor)
    const correctZone = findCorrectZoneIndex(placedEvents, currentEvent, anchor.year)
    const isCorrect = selectedZone === correctZone
    const newStrike = !isCorrect

    // Add event to placed events, then sort chronologically
    const newPlacedEvents = [...placedEvents, { ...currentEvent, wasStrike: newStrike }]
    // Sort by year (oldest first)
    newPlacedEvents.sort((a, b) => a.year - b.year)

    setPlacedEvents(newPlacedEvents)
    setStrikes([...strikes, newStrike])
    setPendingEvents(pendingEvents.slice(1))
    setSelectedZone(null)
    setSelectedZoneId(null)

    // Check if game is complete
    if (pendingEvents.length === 1) {
      // All events placed, move to reveal
      setTimeout(() => {
        setPhase('reveal')
      }, 300)
    }
  }

  const handleRevealComplete = () => {
    // Game is complete - save result
    const accuracy = 4 - strikes.filter((s) => s).length
    const scoreMultipliers = { 4: 1, 3: 0.5, 2: 0, 1: -0.5, 0: -1 }
    const scoreMultiplier = scoreMultipliers[accuracy]
    const dayMultiplier = isSunday() ? DAY_MULTIPLIER.sunday : DAY_MULTIPLIER.weekday
    const payout = wager * scoreMultiplier * dayMultiplier
    const bankedToday = DAILY_CREDIT + payout
    const newWeeklyBank = yesterdaysBank + bankedToday
    const placedEventIds = placedEvents.map((e) => e.id)

    saveDailyResult(wager, accuracy, payout, strikes, newWeeklyBank, placedEventIds)
    setYesterdaysBank(newWeeklyBank)
  }

  const handleGameComplete = () => {
    // User clicked "Back Home" - return to start
    navigate('/')
  }

  if (phase === 'already-played') {
    return <AlreadyPlayedPage
      game={EVENT_PRO_GAME}
      title="You already played today!"
      message="Come back tomorrow for a new puzzle."
      leaderboardHref={`${window.location.origin}/leaderboard`}
      homeHref={getHomeUrl()}
    />
  }

  // Show loading screen
  if (loading || !anchor || !category) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>Loading game...</div>
      </div>
    )
  }

  // Render based on phase
  if (phase === 'wager') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgMuted, padding: '20px 16px' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          {renderHeader()}
          <WagerScreen anchor={anchor} category={category} weeklyBank={yesterdaysBank} onWagerSubmit={handleWagerSubmit} />
        </div>
      </div>
    )
  }

  if (phase === 'playing') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgMuted, padding: '20px 16px 180px' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          {renderHeader()}

          {/* Progress */}
          <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: '#666' }}>
            Event {4 - pendingEvents.length} of 4
          </div>

          {/* Timeline with zones and placed events - chronological order (earliest at bottom) */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '160px' }}>
            {/* Timeline direction indicator */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', color: '#999', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '50px' }}>
              <div style={{ textAlign: 'center' }}>
                <div>📍</div>
                <div style={{ marginTop: '4px', lineHeight: '1.2' }}>Most<br />Recent</div>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '3px' }}>↕</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div>📍</div>
                <div style={{ marginTop: '4px' }}>Oldest</div>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', flex: 1 }}>
            {(() => {
              // Separate events into before and after anchor
              const beforeAnchor = placedEvents.filter(e => e.year < anchor.year).sort((a, b) => b.year - a.year)
              const afterAnchor = placedEvents.filter(e => e.year >= anchor.year).sort((a, b) => b.year - a.year)

              return (
                <>
                  {/* Zone at top (after newest event) */}
                  <PlacementZone
                    index={afterAnchor.length + 1}
                    isSelected={selectedZoneId === 'top'}
                    onClick={() => { setSelectedZone(afterAnchor.length + 1); setSelectedZoneId('top') }}
                    label={afterAnchor.length === 0 ? `▲ After ${formatYear(anchor.year)}` : '▲ After all'}
                  />

                  {/* Events after anchor (newest at top) */}
                  {afterAnchor.map((event, idx) => (
                    <div key={event.id}>
                      <EventCard event={event} isAnchor={false} showYear={false} wasStrike={event.wasStrike} />
                      <PlacementZone
                        index={afterAnchor.length - idx}
                        isSelected={selectedZoneId === `after-${idx}`}
                        onClick={() => { setSelectedZone(afterAnchor.length - idx); setSelectedZoneId(`after-${idx}`) }}
                        label='▼ Between'
                      />
                    </div>
                  ))}

                  {/* Anchor */}
                  <EventCard event={anchor} isAnchor={true} showYear={true} wasStrike={false} />

                  {/* Events before anchor (newest in this group at top) */}
                  {beforeAnchor.map((event, idx) => (
                    <div key={event.id}>
                      <PlacementZone
                        index={-(idx + 1)}
                        isSelected={selectedZoneId === `before-${idx}`}
                        onClick={() => { setSelectedZone(-(idx + 1)); setSelectedZoneId(`before-${idx}`) }}
                        label='▼ Between'
                      />
                      <EventCard event={event} isAnchor={false} showYear={false} wasStrike={event.wasStrike} />
                    </div>
                  ))}

                  {/* Zone at bottom (before oldest event) */}
                  {beforeAnchor.length > 0 && (
                    <PlacementZone
                      index={-(beforeAnchor.length + 1)}
                      isSelected={selectedZoneId === 'bottom'}
                      onClick={() => { setSelectedZone(-(beforeAnchor.length + 1)); setSelectedZoneId('bottom') }}
                      label='▼ Before all'
                    />
                  )}

                  {beforeAnchor.length === 0 && (
                    <PlacementZone
                      index={0}
                      isSelected={selectedZoneId === 'bottom'}
                      onClick={() => { setSelectedZone(0); setSelectedZoneId('bottom') }}
                      label={`▼ Before ${formatYear(anchor.year)}`}
                    />
                  )}
                </>
              )
            })()}
            </div>
          </div>

          {/* Current card at bottom */}
          {currentEvent && <CurrentCard event={currentEvent} selectedZone={selectedZone} onPlaceClick={handlePlaceEvent} isLoading={false} />}
        </div>
      </div>
    )
  }

  if (phase === 'reveal') {
    return (
      <RevealScreen
        anchor={anchor}
        placedEvents={placedEvents}
        strikes={strikes}
        accuracy={4 - strikes.filter((s) => s).length}
        wager={wager}
        weeklyBank={yesterdaysBank}
        onComplete={() => window.location.href = getHomeUrl()}
        onRevealComplete={handleRevealComplete}
      />
    )
  }

  return null
}

export default EventProGame
